<script>
  import { createEventDispatcher, getContext } from 'svelte';
  import { getCurrentUserStore } from '@stores/current_user';

  let currentUser = getCurrentUserStore('default');
  import Modal from '@components/Modal.svelte';
  import Button from '@components/Button.svelte';
  import ReportContentAjax from '@ajax/report_content';
  import BlockedUserAjax from '@ajax/blocked_user';
  import Avatar from '@components/Avatar.svelte';
  import Icon from '@components/Icon.svelte';
  import Notification from '@utils/notification';
  import { BlockedNotificationIconSvg, SettingsIconSvg, ChevronRightIconSvg } from '@utils/icons';
  import Viewport from '@components/Viewport.svelte';

  const dispatch = createEventDispatcher();
  const blockedUserIds = getContext('blockedUsers');
  const reportedContent = getContext('reportedContent');

  export let showModal;
  export let params;
  export let owner;
  export let startStep;
  let currentStep = startStep || 'report'; // 'report', 'reportConfirmation', or 'blockUser'
  let selectedReason = '';

  const reasons = [
    "I don't like it",
    'Harassment or bullying',
    'Violence, hate or exploitation',
    'Suicide, self-harm or eating disorders',
    'Nudity or sexual content',
    'Promoting restricted items',
    'Frauds or scams',
    'Spam content',
    'False information',
  ];

  let close = function () {
    showModal = false;
    currentStep = 'report';
    selectedReason = '';
    dispatch('close');
  };

  let submitReport = async function () {
    const response = await ReportContentAjax.create({ ...params, reason: selectedReason });
    reportedContent.update((store) => {
      const updatedStore = { ...store };
      if (params.rec_object_id) {
        updatedStore[`rec_${params.rec_object_id}`] = response.id;
      }
      if (params.group_base_id) {
        updatedStore[`group_${params.group_base_id}`] = response.id;
      }
      if (params.comment_id) {
        updatedStore[`comment_${params.comment_id}`] = response.id;
      }
      return updatedStore;
    });

    currentUser.update((user) => ({
      ...user,
      reported_content_count: Math.max((user.reported_content_count || 1) + 1, 0),
    }));
    currentStep = 'reportConfirmation';
  };

  let initiateBlockUser = function () {
    currentStep = 'blockUser';
  };

  let isBlocking = false;

  let blockUser = async function () {
    if (isBlocking) return;
    isBlocking = true;

    try {
      await BlockedUserAjax.create({ reason: selectedReason, user_id: owner.id });

      blockedUserIds.update((ids) => [...ids, owner.id]);
      currentUser.set({ ...currentUser, blocked_user_count: (currentUser.blocked_user_count || 0) + 1 });
      Notification.send({
        text: `You blocked ${owner.name}`,
      });
      close();
    } catch (error) {
      console.error('Error blocking user:', error);
      isBlocking = false;
    }
  };
</script>

<style style="text/scss" lang="scss">
  // TODO use typography here
  @import '../../vendor/design-system.scss';
  h2 {
    text-align: center;
    font-weight: 500;
    margin-bottom: 8px;
    font-size: 20px;
    line-height: 28px;
  }

  h5 {
    font-size: 16px;
    line-height: 24px;
  }

  p {
    text-align: center;
    font-size: 14px;
    color: #666;
    margin-bottom: 16px;
  }

  ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
  }

  li:hover {
    font-weight: 500;
  }

  li.selected {
    font-weight: 700;
  }

  .checkmark {
    width: 20px;
    height: 20px;
  }

  .button-container {
    margin-top: 32px;
    width: 100%;
  }

  :global(.report-button, .done-button, .block-button) {
    width: 100%;
    background-color: #000;
    color: #fff;
    border: none;
    padding: 12px;
    border-radius: 8px;
    font-weight: bold;
  }

  .block-user-content {
    text-align: center;

    .block-user-icon {
      position: absolute;
      left: calc(50% + 20px);
      background-color: white;
      border-radius: 20px;
      border: 1px solid white;
      top: calc(50% - 75px);
    }

    .block-user-name {
      font-size: 18px;
      font-weight: bold;
      margin: 20px 0;
    }

    .block-info {
      text-align: left;
      margin: 20px 0;
    }

    .block-info-item {
      display: flex;
      align-items: center;
      margin: 16px 0;
      gap: 16px;
    }

    .block-info-text {
      margin-left: 10px;
    }
  }

  .block-user {
    padding: 8px 0px;
    margin-top: 10px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    position: relative;

    p {
      font-weight: 500;
      font-size: 16px;
      color: #212529;
    }
  }

  .block-user-content {
    :global(.avatar) {
      cursor: default !important;
    }
  }

  :global(.report-content-modal-desktop) {
    max-width: 440px !important;
    min-width: 440px !important;
  }
</style>

<Viewport let:mobile>
  <Modal
    containerClass={mobile ? '' : 'report-content-modal-desktop'}
    on:close={close}
    show={true}
    showCloseButton={true}
    fadeInDuration={200}
    mobile={mobile}>
    >
    <div slot="content" class=" content">
      {#if currentStep === 'report'}
        <h2>Why are you reporting this?</h2>
        <p>Your report will be anonymous.</p>

        <ul>
          {#each reasons as reason}
            <li on:click={() => (selectedReason = reason)} class:selected={selectedReason === reason}>
              {reason}
              {#if selectedReason === reason}
                <img src="/images/report/confirmation.svg" alt="Selected" class="checkmark" />
              {/if}
            </li>
          {/each}
        </ul>

        <div class="button-container">
          <Button
            text="Report"
            colorScheme="black"
            on:click={submitReport}
            disabled={!selectedReason}
            class="report-button" />
        </div>
      {:else if currentStep === 'reportConfirmation'}
        <h2>Thanks for your feedback!</h2>
        <p>We'll take a look at your report and show you less of <br /> this kind of content in the future.</p>

        <h5>Other steps you can take:</h5>
        <div class="block-user" on:click={initiateBlockUser}>
          <p>Block user</p>
          <Icon size={24} path={ChevronRightIconSvg} />
        </div>
        <div class="button-container">
          <Button text="Done" colorScheme="black" on:click={close} class="done-button" />
        </div>
      {:else if currentStep === 'blockUser'}
        <div class="block-user-content" style="cursor: default!important">
          <div class="avatar-container">
            <Avatar width={88} src={owner.avatar_url} showBorder={false} user={owner} />

            <img
              class="block-user-icon"
              width={24}
              height={24}
              src="/images/report/user-block-icon.svg"
              alt="user block" />
          </div>
          <div class="block-user-name">Block {owner.name}?</div>
          <div class="block-info">
            <div class="block-info-item">
              <Icon size={24} path={BlockedNotificationIconSvg} />
              <span class="block-info-text"> They won't be notified that you blocked them </span>
            </div>
            <div class="block-info-item">
              <Icon size={24} path={SettingsIconSvg} />
              <span class="block-info-text"> You can unblock them any time from your account settings </span>
            </div>
          </div>
          <div class="button-container">
            <Button text="Block" colorScheme="black" on:click={blockUser} disabled={isBlocking} class="block-button" />
          </div>
        </div>
      {/if}
    </div>
  </Modal>
</Viewport>
